// controls.ts — the bottom control bar: pick your instrument + drive the beat.

import { hueForUserVoice, PLINK_VOICES, PLINK_VOICE_LABELS, type PlinkVoiceId } from "../audio";
import { GROOVES } from "../beat";

export interface ControlsConfig {
  localUserId: string;
  localHue: number;
  currentVoice: PlinkVoiceId;
  onVoice: (v: PlinkVoiceId) => void;
  currentGroove: string;
  beatPlaying: boolean;
  currentTempo: number;
  /** user pressed the beat button; returns nothing — state comes back via setBeatPlaying */
  onBeatToggle: () => void;
  onGroove: (id: string) => void;
  /** throttled live tempo drag (shared with the room) */
  onTempo: (bpm: number) => void;
}

export interface ControlsHandle {
  /** reflect room beat on/off (e.g. toggled by another player) */
  setBeatPlaying(on: boolean): void;
  /** reflect the room's active groove */
  setActiveGroove(id: string): void;
  /** reflect the room's tempo (e.g. changed by another player) */
  setTempo(bpm: number): void;
}

export function mountControls(root: HTMLElement, cfg: ControlsConfig): ControlsHandle {
  root.innerHTML = "";
  root.hidden = false;
  root.style.setProperty("--accent", `hsl(${cfg.localHue}, 85%, 60%)`);

  // --- beat group ---
  const beatGroup = document.createElement("div");
  beatGroup.className = "ctl-group";

  const beatBtn = document.createElement("button");
  beatBtn.className = "ctl-beat";

  function paintBeat(on: boolean): void {
    beatBtn.classList.toggle("on", on);
    beatBtn.textContent = on ? "■ beat" : "▶ beat";
  }
  paintBeat(cfg.beatPlaying);

  beatBtn.addEventListener("click", () => cfg.onBeatToggle());
  beatGroup.appendChild(beatBtn);

  function setActiveGroove(id: string): void {
    for (const el of beatGroup.querySelectorAll(".ctl-pill")) {
      el.classList.toggle("active", (el as HTMLElement).dataset.groove === id);
    }
  }

  for (const gr of GROOVES) {
    const pill = document.createElement("button");
    pill.className = "ctl-pill";
    pill.dataset.groove = gr.id;
    pill.textContent = gr.label;
    pill.classList.toggle("active", gr.id === cfg.currentGroove);
    pill.addEventListener("click", () => {
      cfg.onGroove(gr.id);
      setActiveGroove(gr.id);
    });
    beatGroup.appendChild(pill);
  }

  // --- voice group ---
  const voiceGroup = document.createElement("div");
  voiceGroup.className = "ctl-group";

  for (const v of PLINK_VOICES) {
    const pill = document.createElement("button");
    pill.className = "ctl-pill voice";
    pill.textContent = PLINK_VOICE_LABELS[v];
    // tint each instrument button with that instrument's colour (matches ripples)
    pill.style.setProperty("--vh", String(hueForUserVoice(cfg.localUserId, v)));
    pill.classList.toggle("active", v === cfg.currentVoice);
    pill.addEventListener("click", () => {
      cfg.onVoice(v);
      for (const el of voiceGroup.querySelectorAll(".ctl-pill")) el.classList.remove("active");
      pill.classList.add("active");
    });
    voiceGroup.appendChild(pill);
  }

  // --- tempo group ---
  const tempoGroup = document.createElement("div");
  tempoGroup.className = "ctl-group tempo";

  const tempoLabel = document.createElement("span");
  tempoLabel.className = "ctl-tempo-label";

  const slider = document.createElement("input");
  slider.className = "ctl-tempo";
  slider.type = "range";
  slider.min = "60";
  slider.max = "180";
  slider.step = "1";

  function paintTempo(bpm: number): void {
    const clamped = Math.max(60, Math.min(180, Math.round(bpm)));
    slider.value = String(clamped);
    tempoLabel.textContent = `${clamped} bpm`;
  }
  paintTempo(cfg.currentTempo);

  // throttle the live drag so we don't flood the relay
  let lastSent = 0;
  let lastVal = cfg.currentTempo;
  slider.addEventListener("input", () => {
    const bpm = Number(slider.value);
    tempoLabel.textContent = `${bpm} bpm`;
    const now = Date.now();
    if (bpm !== lastVal && now - lastSent > 80) {
      lastVal = bpm;
      lastSent = now;
      cfg.onTempo(bpm);
    }
  });
  slider.addEventListener("change", () => cfg.onTempo(Number(slider.value)));

  tempoGroup.appendChild(tempoLabel);
  tempoGroup.appendChild(slider);

  root.appendChild(beatGroup);
  root.appendChild(tempoGroup);
  root.appendChild(voiceGroup);

  return { setBeatPlaying: paintBeat, setActiveGroove, setTempo: paintTempo };
}
